import { useState, useEffect, useCallback, useMemo, Suspense } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { hasAtLeast, ROLES, type Role } from "@wwrp/shared";
import { useAuth } from "../contexts/AuthContext.js";
import { getAccessRole } from "../api/auth.js";
import { IconLogout2, IconSearch } from "@tabler/icons-react";
import classes from "./NavbarSimple.module.css";
import headerClasses from "./Header.module.css";
import { CurrentUser } from "./CurrentUser.js";
import { NotificationsButton } from "./NotificationsButton.js";
import { ThemeButton, useThemeCycle, THEME_ICON, THEME_LABEL } from "./ThemeButton.js";
import { AppSpotlight } from "./AppSpotlight.js";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { spotlight } from "@mantine/spotlight";
import wwrpLogo from "../static/wwrp_logo2.png";
import { NAV_PAGES } from "../nav.js";
import { ROLE_LABEL } from "./RoleBadge.js";

export function Layout() {
  const { user, logout, setViewAsRole, clearViewAsRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  const visibleItems = useMemo(
    () =>
      NAV_PAGES.filter((item) => {
        if (!user) return !item.minRole && !item.permissionKey && !item.sysadminOnly;
        if (item.sysadminOnly) return user.isSysadmin && !user.viewingAsRole;
        if (item.permissionKey) return user.permissions?.[item.permissionKey] ?? false;
        const accessRole = getAccessRole(user);
        return !item.minRole || (accessRole !== null && hasAtLeast(accessRole, item.minRole));
      }),
    [user],
  );

  const { current: themeScheme, cycle: cycleTheme } = useThemeCycle();
  const ThemeIcon = THEME_ICON[themeScheme];
  const canViewAs = !!user && (user.isSysadmin || hasAtLeast(user.trueRole, "head_administration"));
  const isViewingAs = !!user?.viewingAsRole;

  useEffect(() => {
    setSelectedRole(user?.viewingAsRole ?? user?.role ?? null);
  }, [user?.viewingAsRole, user?.role]);

  return (
    <div className="app-shell">
      {isViewingAs && (
        <Box
          style={{
            background: "color-mix(in srgb, var(--warning) 16%, var(--bg-elev))",
            borderBottom: "1px solid color-mix(in srgb, var(--warning) 35%, var(--border))",
            padding: "8px 14px",
          }}
        >
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" fw={600}>
              Role-view mode active: viewing Hub as{" "}
              {ROLE_LABEL[user!.viewingAsRole as Role] ?? user!.viewingAsRole}
            </Text>
            <Group gap="xs">
              {canViewAs && (
                <Button size="xs" variant="light" onClick={() => setViewAsOpen(true)}>
                  Change role
                </Button>
              )}
              <Button
                size="xs"
                color="orange"
                variant="filled"
                onClick={() => void clearViewAsRole()}
              >
                Return to normal
              </Button>
            </Group>
          </Group>
        </Box>
      )}

      <header className={`app-header ${headerClasses.root}`}>
        <button
          className={`hamburger${menuOpen ? " hamburger--open" : ""}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={menuOpen}
          aria-controls="sidebar-nav"
          type="button"
        >
          <span className="hamburger__line" />
          <span className="hamburger__line" />
          <span className="hamburger__line" />
        </button>

        <div className={headerClasses.left}>
          <Link to="/" className={headerClasses.logo} aria-label="WWRP Hub home">
            <img src={wwrpLogo} alt="WWRP Hub" width={921} height={236} />
          </Link>
        </div>
        <div className={headerClasses.rightContent}>
          {canViewAs && (
            <Tooltip label="View Hub as another role" position="bottom" visibleFrom="sm">
              <Button
                size="xs"
                variant={isViewingAs ? "filled" : "light"}
                color={isViewingAs ? "orange" : "brand"}
                onClick={() => setViewAsOpen(true)}
                visibleFrom="sm"
              >
                View as role
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Search (⌘K)" position="bottom" visibleFrom="sm">
            <ActionIcon
              variant="transparent"
              c="inherit"
              size={42}
              onClick={spotlight.open}
              aria-label="Open command palette"
              visibleFrom="sm"
            >
              <IconSearch stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Box visibleFrom="sm">
            <ThemeButton />
          </Box>
          <NotificationsButton />
          <CurrentUser />
        </div>
      </header>

      <div className="app-body">
        <div
          className={`sidebar-backdrop${menuOpen ? " sidebar-backdrop--open" : ""}`}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />

        <nav
          id="sidebar-nav"
          className={`sidebar${menuOpen ? " sidebar--open" : ""} ${classes.navbar}`}
          aria-label="Main navigation"
        >
          <div className={classes.navbarMain}>
            {visibleItems.map((item, index) => {
              const isActive = item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);
              const showSection =
                item.section !== undefined &&
                (index === 0 || visibleItems[index - 1]?.section !== item.section);
              return (
                <div key={item.to}>
                  {showSection && (
                    <Divider
                      my="xs"
                      label={item.section}
                      labelPosition="left"
                      className={classes.sectionDivider}
                      styles={{
                        label: {
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                        },
                      }}
                    />
                  )}
                  <Link to={item.to} className={classes.link} data-active={isActive || undefined}>
                    <item.icon className={classes.linkIcon} stroke={1.5} />
                    <span>{item.label}</span>
                  </Link>
                </div>
              );
            })}
          </div>

          <Box hiddenFrom="sm" className={classes.footer}>
            <button type="button" className={classes.link} onClick={() => spotlight.open()}>
              <IconSearch className={classes.linkIcon} stroke={1.5} />
              <span>Search</span>
            </button>
            <button type="button" className={classes.link} onClick={cycleTheme}>
              <ThemeIcon className={classes.linkIcon} stroke={1.5} />
              <span>{THEME_LABEL[themeScheme]}</span>
            </button>
          </Box>

          <div className={classes.footer}>
            {user && (
              <button type="button" className={classes.link} onClick={() => void handleLogout()}>
                <IconLogout2 className={classes.linkIcon} stroke={1.5} />
                <span>Sign out</span>
              </button>
            )}
          </div>
        </nav>

        <main className="main-content">
          <Suspense
            fallback={
              <Center h="60vh">
                <Loader size="sm" />
              </Center>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>

      <AppSpotlight />

      <Modal opened={viewAsOpen} onClose={() => setViewAsOpen(false)} title="View Hub As Role">
        <Text size="sm" c="dimmed" mb="sm">
          This changes your effective permissions and visibility across the Hub until you return to
          normal.
        </Text>
        <Select
          label="Role"
          data={ROLES.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }))}
          value={selectedRole}
          onChange={(v) => setSelectedRole((v as Role | null) ?? null)}
        />
        <Group justify="space-between" mt="md">
          <Button variant="subtle" onClick={() => setViewAsOpen(false)}>
            Cancel
          </Button>
          <Group>
            <Button
              variant="light"
              color="gray"
              onClick={() => {
                void clearViewAsRole();
                setViewAsOpen(false);
              }}
            >
              Return to normal
            </Button>
            <Button
              onClick={() => {
                if (!selectedRole) return;
                void setViewAsRole(selectedRole);
                setViewAsOpen(false);
              }}
            >
              Apply
            </Button>
          </Group>
        </Group>
      </Modal>
    </div>
  );
}
